import type { VesselCardZnScenario, VesselCardZnSegment } from "../models/vessel-card-zn.models";

export const VESSEL_CARD_ZN_STANDARD_CONDITION = "BF4 / DSS3";
export const VESSEL_CARD_ZN_QUERY_CONDITION = { windKnots: 16, waveHeightMetres: 1.25 } as const;
export const VESSEL_CARD_ZN_FRESHNESS_DAYS = 90;
export const VESSEL_CARD_ZN_DAY_MS = 86_400_000;
export const VESSEL_CARD_ZN_MODES = ["Ballast ECO", "Laden ECO", "Ballast FULL", "Laden FULL"] as const;
export const VESSEL_CARD_ZN_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const VESSEL_CARD_ZN_SCENARIOS: Array<{ value: VesselCardZnScenario; label: string }> = [
  { value: "available", label: "Model available" },
  { value: "stale", label: "Model older than 90 days" },
  { value: "unavailable", label: "No model" },
  { value: "partial", label: "Partial predictions" },
  { value: "missingTraining", label: "Unverified training date" },
];

// Same agreed draft references as the backend; used only to describe fixture inputs.
export const VESSEL_CARD_ZN_SEGMENTS: readonly VesselCardZnSegment[] = [
  { name: "Handy", minimumDwt: 18_000, maximumDwt: 50_000, ballastDraft: 5.5, ladenDraft: 10 },
  { name: "Supramax", minimumDwt: 50_000, maximumDwt: 66_000, ballastDraft: 6, ladenDraft: 13 },
  { name: "Kamsarmax", minimumDwt: 66_000, maximumDwt: 100_000, ballastDraft: 6.5, ladenDraft: 14 },
  { name: "Baby Cape", minimumDwt: 100_000, maximumDwt: 130_000, ballastDraft: 7.5, ladenDraft: 14.5 },
  { name: "Cape", minimumDwt: 130_000, maximumDwt: 220_001, ballastDraft: 9, ladenDraft: 18 },
];

export const VESSEL_CARD_ZN_UNAVAILABLE_REASONS: Record<string, string> = {
  table_failure: "Stored ZN points could not be retrieved.",
  table_points_ambiguous: "Stored ZN points are ambiguous for this vessel.",
  stored_point_unavailable: "No exact stored ZN point is available for these conditions.",
  stored_point_ambiguous: "More than one stored ZN point matches these conditions.",
  stored_point_disabled: "The stored ZN point is not enabled.",
  stored_consumption_unavailable: "Stored main-engine or normal auxiliary consumption is unavailable.",
  stored_units_unsupported: "Stored ZN units are unsupported.",
  stored_model_version_unavailable: "The stored ZN model version is unavailable.",
  expected_speed_unavailable: "Expected Performance speed is unavailable.",
  request_aborted: "Loading stored ZN points was cancelled.",
  // Retained for explicit fixtures and the parked API contract, not active table configuration.
  connector_not_configured: "ZN connection is not configured.",
  training_date_unverified: "A verified per-vessel training date is not available.",
  model_stale: "Model training is older than 90 days.",
  draft_reference_unavailable: "A draft reference requires summer DWT between 18,000 and 220,000.",
  model_not_enabled: "No enabled model is available for this vessel.",
  operating_limits_unavailable: "Model operating limits are unavailable.",
  auxiliary_unavailable: "Normal sailing auxiliary consumption is unavailable.",
  model_version_unavailable: "Model version information is unavailable.",
  predictions_unavailable: "No usable fuel predictions are available.",
  api_failure: "ZN fuel model data could not be retrieved.",
};

// Synthetic differences from the slide, not fitted ZeroNorth coefficients.
export const VESSEL_CARD_ZN_MOCK_FACTORS: Record<string, number> = {
  "Ballast ECO": 0.98,
  "Laden ECO": 1.015,
  "Ballast FULL": 0.98,
  "Laden FULL": 1.019,
};
